import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  Loader2, 
  AlertTriangle,
  ArrowLeft,
  XCircle
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, SectionHeader } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * Step 6: Supprimer le projet
 * ===========================
 * Confirmation modal with type-to-confirm safeguard
 */
function DeleteStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  useEffect(() => {
    loadProject();
  }, [projectId]);
  
  const loadProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du projet');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (confirmText !== 'DELETE' && confirmText !== project?.name) {
      toast.error('Tapez DELETE ou le nom du projet pour confirmer');
      return;
    }
    
    setDeleting(true);
    try {
      await apiClient.delete(`/projects/${projectId}`);
      toast.success('Projet supprimé avec succès');
      navigate('/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      
      if (error.response?.status === 404) {
        // Project already deleted
        navigate('/dashboard');
      }
    } finally {
      setDeleting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(var(--color-primary))' }} />
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto">
      <SectionHeader
        title="Supprimer le projet"
        subtitle="Cette action est irréversible"
      />
      
      {!showConfirm ? (
        /* Initial Warning Screen */
        <GlassCard className="p-8 text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'hsl(var(--color-danger-bg))' }}
          >
            <Trash2 className="w-10 h-10" style={{ color: 'hsl(var(--color-danger))' }} />
          </div>
          
          <h3 className="text-2xl font-bold text-semantic mb-2">
            Supprimer &quot;{project?.name}&quot; ?
          </h3>
          
          <p className="text-semantic-2 mb-8">
            Cette action supprimera définitivement le projet ainsi que tous les contenus, 
            publications et données analytiques associés.
          </p>
          
          <div 
            className="p-4 rounded-xl mb-8 text-left"
            style={{ 
              backgroundColor: 'hsl(var(--color-warning-bg))',
              border: '1px solid hsl(var(--color-warning) / 0.3)'
            }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--color-warning))' }} />
              <div>
                <p className="font-medium" style={{ color: 'hsl(var(--color-warning-text))' }}>
                  Attention : Cette action est irréversible
                </p>
                <ul className="text-sm mt-2 space-y-1" style={{ color: 'hsl(var(--color-warning-text))' }}>
                  <li>• Tous les contenus générés seront supprimés</li>
                  <li>• L'historique des publications sera perdu</li>
                  <li>• Les connexions aux plateformes seront déconnectées</li>
                  <li>• Les données analytiques seront effacées</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <PillButton
              variant="secondary"
              className="flex-1"
              onClick={() => navigate(-1)}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Annuler
            </PillButton>
            <PillButton
              variant="primary"
              className="flex-1"
              onClick={() => setShowConfirm(true)}
              style={{ 
                backgroundColor: 'hsl(var(--color-danger))',
                color: 'white'
              }}
            >
              Continuer la suppression
            </PillButton>
          </div>
        </GlassCard>
      ) : (
        /* Confirmation Screen with Type-to-Confirm */
        <GlassCard className="p-8">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'hsl(var(--color-danger-bg))' }}
          >
            <XCircle className="w-8 h-8" style={{ color: 'hsl(var(--color-danger))' }} />
          </div>
          
          <h3 className="text-xl font-bold text-semantic text-center mb-2">
            Confirmation requise
          </h3>
          
          <p className="text-semantic-2 text-center mb-6">
            Pour confirmer la suppression, tapez <strong className="text-semantic">DELETE</strong> ou le nom du projet :
          </p>
          
          <div 
            className="p-4 rounded-xl mb-6 text-center"
            style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
          >
            <p className="text-xs text-semantic-muted mb-1">Nom du projet</p>
            <p className="text-lg font-semibold text-semantic">{project?.name}</p>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-semantic mb-2">
              Tapez DELETE ou le nom du projet pour confirmer
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full input-semantic text-center text-lg"
              autoFocus
              data-testid="delete-confirm-input"
            />
          </div>
          
          <div className="flex gap-4">
            <PillButton
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowConfirm(false);
                setConfirmText('');
              }}
            >
              Annuler
            </PillButton>
            <PillButton
              variant="primary"
              className="flex-1"
              onClick={handleDelete}
              loading={deleting}
              disabled={confirmText !== 'DELETE' && confirmText !== project?.name}
              style={{ 
                backgroundColor: (confirmText === 'DELETE' || confirmText === project?.name) 
                  ? 'hsl(var(--color-danger))' 
                  : 'hsl(var(--color-danger) / 0.5)',
                color: 'white'
              }}
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Suppression...' : 'Supprimer définitivement'}
            </PillButton>
          </div>
        </GlassCard>
      )}
    </div>
  );
}

/**
 * Delete Step Page Wrapper
 */
export default function DeleteStep() {
  return (
    <GuidedProjectLayout>
      <DeleteStepContent />
    </GuidedProjectLayout>
  );
}
